import { lazy } from 'react';

import { pluginFlags } from './pluginFlags';

/*
 * Route-level code splitting: every page is loaded as its own chunk on
 * demand instead of inflating the entry bundle. A Suspense boundary
 * around the dashboard outlet (see DashboardLayout) shows a fallback
 * while a chunk loads. `lazyNamed` adapts our named page exports to the
 * default-export shape React.lazy expects.
 */
const lazyNamed = <Module extends Record<string, unknown>>(
  loader: () => Promise<Module>,
  name: keyof Module
) =>
  lazy(() =>
    loader().then((module) => ({
      default: module[name] as React.ComponentType,
    }))
  );

const OverviewPage = lazyNamed(
  () => import('../features/overview/page/OverviewPage'),
  'OverviewPage'
);
const WhatsNewPage = lazyNamed(
  () => import('../features/overview/page/WhatsNewPage'),
  'WhatsNewPage'
);
const PartnerPage = lazyNamed(
  () => import('../features/partner/page/PartnerPage'),
  'PartnerPage'
);
const PartnerCreate = lazyNamed(
  () => import('../features/partner/page/PartnerCreate'),
  'PartnerCreate'
);
const PartnerUpdate = lazyNamed(
  () => import('../features/partner/page/PartnerUpdate'),
  'PartnerUpdate'
);
const MembershipPage = lazyNamed(
  () => import('../features/membership/page/MembershipPage'),
  'MembershipPage'
);
const MembershipCreate = lazyNamed(
  () => import('../features/membership/page/MembershipCreate'),
  'MembershipCreate'
);
const MembershipUpdate = lazyNamed(
  () => import('../features/membership/page/MembershipUpdate'),
  'MembershipUpdate'
);
const DebitorCreate = lazyNamed(
  () => import('../features/debitor/page/DebitorCreate'),
  'DebitorCreate'
);
const DebitorUpdate = lazyNamed(
  () => import('../features/debitor/page/DebitorUpdate'),
  'DebitorUpdate'
);
const DebitorsPage = lazyNamed(
  () => import('../features/debitor/page/DebitorsPage'),
  'DebitorsPage'
);
const ProjectsPage = lazyNamed(
  () => import('../features/project/page/ProjectsPage'),
  'ProjectsPage'
);
const BookingItemsPage = lazyNamed(
  () => import('../features/booking/page/BookingItemsPage'),
  'BookingItemsPage'
);
const BookingItemCreate = lazyNamed(
  () => import('../features/booking/page/BookingItemCreate'),
  'BookingItemCreate'
);
const HostingAssetsPage = lazyNamed(
  () => import('../features/hosting/page/HostingAssetsPage'),
  'HostingAssetsPage'
);
const HostingAssetCreate = lazyNamed(
  () => import('../features/hosting/page/HostingAssetCreate'),
  'HostingAssetCreate'
);

const Contacts = lazyNamed(
  () => import('../features/contacts/page/Contacts'),
  'Contacts'
);
const ContactUpdate = lazyNamed(
  () => import('../features/contacts/page/ContactUpdate'),
  'ContactUpdate'
);
const ContactCreate = lazyNamed(
  () => import('../features/contacts/page/ContactCreate'),
  'ContactCreate'
);

const MailingListPage = lazyNamed(
  () => import('../features/mailinglist/page/MailingListPage'),
  'MailingListPage'
);
const MailingListCreate = lazyNamed(
  () => import('../features/mailinglist/page/MailingListCreate'),
  'MailingListCreate'
);
const PersonsPage = lazyNamed(
  () => import('../features/persons/page/PersonsPage'),
  'PersonsPage'
);
const PersonCreate = lazyNamed(
  () => import('../features/persons/page/PersonCreate'),
  'PersonCreate'
);
const PersonUpdate = lazyNamed(
  () => import('../features/persons/page/PersonUpdate'),
  'PersonUpdate'
);
const BankDirectoryPage = lazyNamed(
  () => import('../features/bankdirectory/page/BankDirectoryPage'),
  'BankDirectoryPage'
);
const BankAccountsPage = lazyNamed(
  () => import('../features/bankaccounts/page/BankAccountsPage'),
  'BankAccountsPage'
);
const BankAccountCreate = lazyNamed(
  () => import('../features/bankaccounts/page/BankAccountCreate'),
  'BankAccountCreate'
);

const GrantsPage = lazyNamed(
  () => import('../features/rights/page/GrantsPage'),
  'GrantsPage'
);
const GrantCreate = lazyNamed(
  () => import('../features/rights/page/GrantCreate'),
  'GrantCreate'
);

const User = lazyNamed(() => import('../pages/User'), 'User');
const ConfigPage = lazyNamed(
  () => import('../features/config/page/ConfigPage'),
  'ConfigPage'
);

const availablePlugins = [
  {
    /* core area, always enabled — the personal user page, listed in
       the personal menu section at the bottom of the sidebar */
    key: 'user',
    enabled: true,
    dashboardRoutes: [
      {
        path: 'user',
        element: <User />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/user',
        labelKey: 'nav.user',
        group: 'personal',
        /* bi-person, the same icon the topbar shrinks to on phones */
        iconClass: 'nav-user',
      },
    ],
  },
  {
    /* core area, always enabled — client-side configuration, listed
       between the user entry and the logout action */
    key: 'config',
    enabled: true,
    dashboardRoutes: [
      {
        path: 'config',
        element: <ConfigPage />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/config',
        labelKey: 'nav.config',
        group: 'personal',
        /* bi-gear */
        iconClass: 'nav-config',
      },
    ],
  },
  {
    /* core area, always enabled — the dashboard landing page; listed
       before the flag-gated plugins so its defaultDashboardPath wins */
    key: 'overview',
    enabled: true,
    defaultDashboardPath: 'overview',
    dashboardRoutes: [
      {
        path: 'overview',
        element: <OverviewPage />,
      },
      /* reached from the card that shows the newest few — no menu
         entry of its own, it is the long form of one dashboard card */
      {
        path: 'whats-new',
        element: <WhatsNewPage />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/overview',
        labelKey: 'nav.overview',
        /* no group: rendered above the grouped menu sections */
        /* bi-speedometer2 */
        iconClass: 'nav-overview',
      },
    ],
  },
  {
    /* core area, always enabled — not gated by a plugin flag */
    key: 'partner',
    enabled: true,
    dashboardRoutes: [
      {
        path: 'partner',
        element: <PartnerPage />,
      },
      {
        path: 'partner-create',
        element: <PartnerCreate />,
      },
      {
        path: 'partner-update/:uuid',
        element: <PartnerUpdate />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/partner',
        labelKey: 'nav.partner',
        group: 'administration',
        /* bi-building */
        iconClass: 'nav-partner',
        /* the create/update routes count as the partner area, too */
        matchPath: '/dashboard/partner',
      },
    ],
  },
  {
    /* core area, always enabled — not gated by a plugin flag */
    key: 'membership',
    enabled: true,
    dashboardRoutes: [
      {
        path: 'membership',
        element: <MembershipPage />,
      },
      {
        path: 'membership-create',
        element: <MembershipCreate />,
      },
      {
        path: 'membership-update/:uuid',
        element: <MembershipUpdate />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/membership',
        labelKey: 'nav.membership',
        group: 'administration',
        /* bi-award */
        iconClass: 'nav-membership',
        /* the create/update routes count as the membership area, too */
        matchPath: '/dashboard/membership',
      },
    ],
  },
  {
    /* core area, always enabled — not gated by a plugin flag */
    key: 'debitors',
    enabled: true,
    dashboardRoutes: [
      {
        path: 'debitors',
        element: <DebitorsPage />,
      },
      {
        path: 'debitor-create',
        element: <DebitorCreate />,
      },
      {
        path: 'debitor-update/:uuid',
        element: <DebitorUpdate />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/debitors',
        labelKey: 'nav.debitors',
        group: 'administration',
        /* the create/update routes count as the debitor area, too */
        matchPath: '/dashboard/debitor',
        /* bi-receipt */
        iconClass: 'nav-debitors',
      },
    ],
  },
  {
    /* core area (preview), always enabled — not gated by a plugin flag */
    key: 'bookingProjects',
    enabled: true,
    dashboardRoutes: [
      {
        path: 'projects',
        element: <ProjectsPage />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/projects',
        labelKey: 'nav.projects',
        group: 'hosting',
        /* bi-diagram-3 */
        iconClass: 'nav-projects',
      },
    ],
  },
  {
    /* core area (preview), always enabled — not gated by a plugin flag */
    key: 'bookingItems',
    enabled: true,
    dashboardRoutes: [
      {
        path: 'bookings',
        element: <BookingItemsPage />,
      },
      {
        path: 'booking-create',
        element: <BookingItemCreate />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/bookings',
        labelKey: 'nav.bookings',
        group: 'hosting',
        /* bi-journal-check */
        iconClass: 'nav-bookings',
        /* the create route counts as the bookings area, too */
        matchPath: '/dashboard/booking',
      },
    ],
  },
  {
    /* core area (preview), always enabled — not gated by a plugin flag */
    key: 'hostingAssets',
    enabled: true,
    dashboardRoutes: [
      {
        path: 'hosting-assets',
        element: <HostingAssetsPage />,
      },
      {
        path: 'hosting-asset-create',
        element: <HostingAssetCreate />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/hosting-assets',
        labelKey: 'nav.hostingAssets',
        group: 'hosting',
        /* bi-hdd-stack */
        iconClass: 'nav-hosting-assets',
        /* the create route counts as the hosting-assets area, too */
        matchPath: '/dashboard/hosting-asset',
      },
    ],
  },
  {
    key: 'contacts',
    enabled: pluginFlags.contact,
    dashboardRoutes: [
      {
        path: 'contacts',
        element: <Contacts />,
      },
      {
        path: 'contact-create',
        element: <ContactCreate />,
      },
      {
        path: 'contact-update/:uuid',
        element: <ContactUpdate />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/contacts',
        labelKey: 'nav.contacts',
        group: 'administration',
        /* bi-person-lines-fill */
        iconClass: 'nav-contacts',
        /* create/update routes count as the contacts area, too */
        matchPath: '/dashboard/contact',
      },
    ],
  },
  {
    /* core area, always enabled — not gated by a plugin flag */
    key: 'persons',
    enabled: true,
    dashboardRoutes: [
      {
        path: 'persons',
        element: <PersonsPage />,
      },
      {
        path: 'person-create',
        element: <PersonCreate />,
      },
      {
        path: 'person-update/:uuid',
        element: <PersonUpdate />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/persons',
        labelKey: 'nav.persons',
        group: 'administration',
        /* bi-people */
        iconClass: 'nav-persons',
        /* the update route counts as the persons area, too */
        matchPath: '/dashboard/person',
      },
    ],
  },
  {
    /* core area, always enabled — not gated by a plugin flag. No
       update route: a bank account cannot be changed (BE-4) */
    key: 'bankaccounts',
    enabled: true,
    dashboardRoutes: [
      {
        path: 'bankaccounts',
        element: <BankAccountsPage />,
      },
      {
        path: 'bankaccount-create',
        element: <BankAccountCreate />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/bankaccounts',
        labelKey: 'nav.bankAccounts',
        group: 'administration',
        /* bi-bank */
        iconClass: 'nav-bankaccounts',
        /* the create route counts as the accounts area, too */
        matchPath: '/dashboard/bankaccount',
      },
    ],
  },
  {
    /* core area, always enabled; the menu shows it to global admins only */
    key: 'bankdirectory',
    enabled: true,
    dashboardRoutes: [
      {
        path: 'bankdirectory',
        element: <BankDirectoryPage />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/bankdirectory',
        labelKey: 'nav.bankDirectory',
        group: 'administration',
        /* bi-journal-text */
        iconClass: 'nav-bankdirectory',
        globalAdminOnly: true,
      },
    ],
  },
  {
    key: 'mailinglist',
    enabled: pluginFlags.mailinglist,
    dashboardRoutes: [
      {
        path: 'mailinglist',
        element: <MailingListPage />,
      },
      {
        path: 'mailinglist-create',
        element: <MailingListCreate />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/mailinglist',
        labelKey: 'nav.mailinglists',
        group: 'administration',
        /* bi-envelope */
        iconClass: 'nav-mailinglists',
        /* the create route counts as the mailing list area, too */
        matchPath: '/dashboard/mailinglist',
      },
    ],
  },
  {
    /* core area, always enabled — not gated by a plugin flag */
    key: 'rights',
    enabled: true,
    dashboardRoutes: [
      {
        path: 'grants',
        element: <GrantsPage />,
      },
      {
        path: 'grant-create',
        element: <GrantCreate />,
      },
    ],
    dashboardNavItems: [
      {
        to: '/dashboard/grants',
        labelKey: 'nav.grants',
        group: 'rights',
        /* bi-key */
        iconClass: 'nav-grants',
        /* the create route counts as the grants area, too */
        matchPath: '/dashboard/grant',
      },
    ],
  },
];

export const activePlugins = availablePlugins.filter(
  (plugin) => plugin.enabled
);

export const dashboardPluginRoutes = activePlugins.flatMap(
  (plugin) => plugin.dashboardRoutes ?? []
);

export const dashboardPluginNavItems = activePlugins.flatMap(
  (plugin) => plugin.dashboardNavItems ?? []
);

/*
 * If the requested route does not match any active dashboard route,
 * the dashboard routing logic falls back to the configured default dashboard page.
 */
export const defaultDashboardPath =
  activePlugins.find((plugin) => plugin.defaultDashboardPath)
    ?.defaultDashboardPath ?? null;
